/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { useNavigationTableSort } from "components/scrapEyeDataDashboard/navigation/useNavigationTableSort";
import { identifierQuery } from "components/scrapEyeDataDashboard/navigation/queryCodecs";
import { useNavigationQuery } from "components/scrapEyeDataDashboard/navigation/NavigationQueryProvider";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { SortableHeaderCell } from "components/common/SortableHeaderCell";
import { useNotifyStatus } from "contexts/status";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { type SortValueGetters } from "hooks/useTableSort";
import { useCallback, useMemo, useState } from "react";
import {
  useAddAngleToGroupMutation,
  useCreateVideoPlayerGroupMutation,
  useDeleteVideoPlayerGroupMutation,
  useGetVideoPlayerGroupQuery,
  useListVideoPlayerGroupsQuery,
  useListAvailableCameraAnglesForLocationQuery,
  useListInstallationLocationsQuery,
  useRemoveAngleFromGroupMutation,
  useUpdateAnglePriorityMutation,
  type ScrapEyeRecordingPosition,
  type ScrapEyeInstallationVideoPlayerGroupWithAngles,
} from "src/store/api/generatedApi";

type VideoPlayerGroupColumn = "name" | "location" | "ordering";

const videoPlayerGroupSortValueGetters: SortValueGetters<
  ScrapEyeInstallationVideoPlayerGroupWithAngles,
  VideoPlayerGroupColumn
> = {
  name: (group) => group.name,
  location: (group) => group.location_name,
  ordering: (group) => group.ordering,
};

type GroupAngleColumn = "angle" | "location" | "priority";

const groupAngleSortValueGetters: SortValueGetters<
  ScrapEyeRecordingPosition,
  GroupAngleColumn
> = {
  angle: (angle) => angle.name,
  location: (angle) => angle.location_name,
  priority: (angle) => angle.priority,
};

const GroupAnglesTable = ({
  angles,
  onUpdatePriority,
  onRemoveAngle,
}: Readonly<{
  angles: readonly ScrapEyeRecordingPosition[];
  onUpdatePriority: (angleId: number, priority: number) => void;
  onRemoveAngle: (angleId: number) => void;
}>) => {
  const { t } = useTenantTranslation();
  const { sortedRows, sort, toggleSort } = useNavigationTableSort({
    key: "playerAngleOrder",
    rows: angles,
    sortValueGetters: groupAngleSortValueGetters,
    initialSort: { column: "priority", direction: "asc" },
  });

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <SortableHeaderCell column="angle" sort={sort} onSort={toggleSort}>
            {t("angle")}
          </SortableHeaderCell>
          <SortableHeaderCell column="location" sort={sort} onSort={toggleSort}>
            {t("location")}
          </SortableHeaderCell>
          <SortableHeaderCell column="priority" sort={sort} onSort={toggleSort}>
            {t("anglePriorityLabel")}
          </SortableHeaderCell>
          <TableCell align="right">{t("actions")}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {sortedRows.map((angle) => (
          <TableRow key={angle.id}>
            <TableCell>{angle.name}</TableCell>
            <TableCell>{angle.location_name}</TableCell>
            <TableCell>
              <TextField
                type="number"
                size="small"
                value={angle.priority}
                onChange={(e) => {
                  onUpdatePriority(
                    angle.id,
                    Number.parseInt(e.target.value, 10)
                  );
                }}
                sx={{ width: 80 }}
              />
            </TableCell>
            <TableCell align="right">
              <IconButton
                size="small"
                onClick={() => {
                  onRemoveAngle(angle.id);
                }}
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export function VideoPlayerGroupsSection() {
  const tenantName = useTenant();
  const { t } = useTenantTranslation();
  const notifyStatus = useNotifyStatus();

  const [createGroupLocation, setCreateGroupLocation] = useState("");
  const [createGroupName, setCreateGroupName] = useState("");
  const [createGroupOrdering, setCreateGroupOrdering] = useState("1");
  const [manageDialogGroupId, setManageDialogGroupId] = useNavigationQuery(
    "playerGroup",
    null,
    identifierQuery,
    "push"
  );
  const [addAngleId, setAddAngleId] = useState("");
  const [addAnglePriority, setAddAnglePriority] = useState("1");

  const { data: locations } = useListInstallationLocationsQuery({ tenantName });
  const { data: videoPlayerGroups, isLoading } = useListVideoPlayerGroupsQuery({
    tenantName,
  });

  const [createVideoPlayerGroup] = useCreateVideoPlayerGroupMutation();
  const [deleteVideoPlayerGroup] = useDeleteVideoPlayerGroupMutation();
  const [addAngleToGroup] = useAddAngleToGroupMutation();
  const [removeAngleFromGroup] = useRemoveAngleFromGroupMutation();
  const [updateAnglePriority] = useUpdateAnglePriorityMutation();

  const { data: manageDialogGroup } = useGetVideoPlayerGroupQuery(
    {
      tenantName,
      groupId: manageDialogGroupId ?? 0,
    },
    { skip: manageDialogGroupId === null }
  );

  const {
    sortedRows: sortedVideoPlayerGroups,
    sort: videoPlayerGroupSort,
    toggleSort: toggleVideoPlayerGroupSort,
  } = useNavigationTableSort({
    key: "playerGroupOrder",
    rows: videoPlayerGroups,
    sortValueGetters: videoPlayerGroupSortValueGetters,
    initialSort: null,
  });

  const { data: availableAnglesForGroup } =
    useListAvailableCameraAnglesForLocationQuery(
      {
        tenantName,
        locationName: manageDialogGroup?.location_name ?? "",
      },
      { skip: manageDialogGroup == null }
    );
  const filteredAvailableAnglesForGroup = useMemo(() => {
    return availableAnglesForGroup?.filter(
      (angle) => !manageDialogGroup?.angles.some((a) => a.id === angle.id)
    );
  }, [availableAnglesForGroup, manageDialogGroup]);

  const handleCreateGroup = useCallback(async () => {
    if (!createGroupLocation || !createGroupName) {
      notifyStatus({
        text: t("videoPlayerGroupCreateError"),
        type: "error",
      });
      return;
    }

    try {
      await createVideoPlayerGroup({
        tenantName,
        scrapEyeInstallationVideoPlayerGroupCreate: {
          name: createGroupName,
          location_name: createGroupLocation,
          ordering: Number.parseInt(createGroupOrdering, 10),
        },
      }).unwrap();

      notifyStatus({
        text: t("videoPlayerGroupCreateSuccess"),
        type: "success",
      });
      setCreateGroupName("");
      setCreateGroupOrdering("1");
    } catch {
      notifyStatus({
        text: t("videoPlayerGroupCreateError"),
        type: "error",
      });
    }
  }, [
    createGroupLocation,
    createGroupName,
    createGroupOrdering,
    createVideoPlayerGroup,
    tenantName,
    notifyStatus,
    t,
  ]);

  const handleDeleteGroup = useCallback(
    async (groupId: number) => {
      try {
        await deleteVideoPlayerGroup({ tenantName, groupId }).unwrap();
        notifyStatus({
          text: t("videoPlayerGroupDeleteSuccess"),
          type: "success",
        });
      } catch {
        notifyStatus({
          text: t("videoPlayerGroupDeleteError"),
          type: "error",
        });
      }
    },
    [deleteVideoPlayerGroup, tenantName, notifyStatus, t]
  );

  const handleAddAngle = useCallback(async () => {
    if (!manageDialogGroupId || !addAngleId) return;

    try {
      await addAngleToGroup({
        tenantName,
        groupId: manageDialogGroupId,
        scrapEyeInstallationVideoPlayerGroupMembershipCreate: {
          camera_angle_id: Number.parseInt(addAngleId, 10),
          priority: Number.parseInt(addAnglePriority, 10),
        },
      }).unwrap();

      notifyStatus({
        text: "Angle added to group successfully",
        type: "success",
      });
      setAddAngleId("");
      setAddAnglePriority("1");
    } catch {
      notifyStatus({
        text: "Failed to add angle to group",
        type: "error",
      });
    }
  }, [
    manageDialogGroupId,
    addAngleId,
    addAnglePriority,
    addAngleToGroup,
    tenantName,
    notifyStatus,
  ]);

  const handleRemoveAngle = useCallback(
    async (angleId: number) => {
      if (!manageDialogGroupId) return;

      try {
        await removeAngleFromGroup({
          tenantName,
          groupId: manageDialogGroupId,
          angleId,
        }).unwrap();

        notifyStatus({
          text: "Angle removed from group successfully",
          type: "success",
        });
      } catch {
        notifyStatus({
          text: "Failed to remove angle from group",
          type: "error",
        });
      }
    },
    [manageDialogGroupId, removeAngleFromGroup, tenantName, notifyStatus]
  );

  const handleUpdatePriority = useCallback(
    async (angleId: number, newPriority: number) => {
      if (!manageDialogGroupId) return;

      try {
        await updateAnglePriority({
          tenantName,
          groupId: manageDialogGroupId,
          angleId,
          priority: newPriority,
        }).unwrap();

        notifyStatus({
          text: "Priority updated successfully",
          type: "success",
        });
      } catch {
        notifyStatus({
          text: "Failed to update priority",
          type: "error",
        });
      }
    },
    [manageDialogGroupId, updateAnglePriority, tenantName, notifyStatus]
  );

  return (
    <Card>
      <CardHeader title={t("videoPlayerGroupsTitle")} />
      <CardContent>
        <Stack spacing={3}>
          {/* Create Group Form */}
          <Box>
            <Typography variant="h6" gutterBottom>
              {t("createVideoPlayerGroup")}
            </Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                select
                label={t("location")}
                value={createGroupLocation}
                onChange={(e) => {
                  setCreateGroupLocation(e.target.value);
                }}
                size="small"
                sx={{ minWidth: 200 }}
              >
                {locations?.map((loc) => (
                  <MenuItem key={loc.name} value={loc.name}>
                    {loc.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label={t("videoPlayerGroupNameLabel")}
                value={createGroupName}
                onChange={(e) => {
                  setCreateGroupName(e.target.value);
                }}
                size="small"
                sx={{ minWidth: 200 }}
              />
              <TextField
                label={t("videoPlayerGroupOrderingLabel")}
                type="number"
                value={createGroupOrdering}
                onChange={(e) => {
                  setCreateGroupOrdering(e.target.value);
                }}
                size="small"
                sx={{ width: 100 }}
              />
              <Button
                variant="contained"
                onClick={() => void handleCreateGroup()}
                disabled={!createGroupLocation || !createGroupName}
              >
                {t("create")}
              </Button>
            </Stack>
          </Box>

          {/* Angle Groups Table */}
          {isLoading ? (
            <CircularProgress />
          ) : sortedVideoPlayerGroups && sortedVideoPlayerGroups.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <SortableHeaderCell
                    column="name"
                    sort={videoPlayerGroupSort}
                    onSort={toggleVideoPlayerGroupSort}
                  >
                    {t("videoPlayerGroupNameLabel")}
                  </SortableHeaderCell>
                  <SortableHeaderCell
                    column="location"
                    sort={videoPlayerGroupSort}
                    onSort={toggleVideoPlayerGroupSort}
                  >
                    {t("location")}
                  </SortableHeaderCell>
                  <SortableHeaderCell
                    column="ordering"
                    sort={videoPlayerGroupSort}
                    onSort={toggleVideoPlayerGroupSort}
                  >
                    {t("videoPlayerGroupOrderingLabel")}
                  </SortableHeaderCell>
                  <TableCell>{t("videoPlayerGroupAnglesLabel")}</TableCell>
                  <TableCell align="right">{t("actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedVideoPlayerGroups.map((group) => (
                  <TableRow key={group.id}>
                    <TableCell>{group.name}</TableCell>
                    <TableCell>{group.location_name}</TableCell>
                    <TableCell>{group.ordering}</TableCell>
                    <TableCell>
                      {/* Show count of angles */}
                      {/* We'd need to fetch individual groups to show this */}
                      <Button
                        size="small"
                        onClick={() => {
                          setManageDialogGroupId(group.id);
                        }}
                      >
                        {t("manageAngles")}
                      </Button>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => void handleDeleteGroup(group.id)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography color="text.secondary">
              {t("videoPlayerGroupsEmpty")}
            </Typography>
          )}
        </Stack>

        {/* Manage Angles Dialog */}
        <Dialog
          open={manageDialogGroupId !== null}
          onClose={() => {
            setManageDialogGroupId(null);
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>{t("videoPlayerGroupManageDialogTitle")}</DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 2 }}>
              {manageDialogGroup ? (
                <>
                  <Typography variant="body2">
                    <strong>{t("group")}:</strong> {manageDialogGroup.name} (
                    {manageDialogGroup.location_name})
                  </Typography>

                  {/* Current Angles */}
                  {manageDialogGroup.angles.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        {t("videoPlayerGroupAnglesLabel")}
                      </Typography>
                      <GroupAnglesTable
                        key={manageDialogGroup.id}
                        angles={manageDialogGroup.angles}
                        onUpdatePriority={(angleId, priority) => {
                          void handleUpdatePriority(angleId, priority);
                        }}
                        onRemoveAngle={(angleId) => {
                          void handleRemoveAngle(angleId);
                        }}
                      />
                    </Box>
                  )}

                  {/* Add Angle Form */}
                  {filteredAvailableAnglesForGroup &&
                  filteredAvailableAnglesForGroup.length > 0 ? (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        {t("addAngleToGroup")}
                      </Typography>
                      <Stack direction="row" spacing={2}>
                        <TextField
                          select
                          label={t("angle")}
                          value={addAngleId}
                          onChange={(e) => {
                            setAddAngleId(e.target.value);
                          }}
                          size="small"
                          sx={{ minWidth: 200 }}
                        >
                          {filteredAvailableAnglesForGroup?.map((angle) => (
                            <MenuItem
                              key={angle.id}
                              value={angle.id.toString()}
                            >
                              {angle.location_name}: {angle.name}
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          label={t("anglePriorityLabel")}
                          type="number"
                          value={addAnglePriority}
                          onChange={(e) => {
                            setAddAnglePriority(e.target.value);
                          }}
                          size="small"
                          sx={{ width: 100 }}
                        />
                        <Button
                          variant="contained"
                          onClick={() => void handleAddAngle()}
                          disabled={!addAngleId}
                        >
                          {t("add")}
                        </Button>
                      </Stack>
                    </Box>
                  ) : null}
                </>
              ) : null}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setManageDialogGroupId(null);
              }}
            >
              {t("close")}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
}
